import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

export interface Category {
  id: string;
  name: string;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Get all categories
 */
export const getCategories = async (): Promise<Category[]> => {
  try {
    console.log('Fetching categories from Firestore...');
    const categoriesCollection = collection(db, 'categories');
    const snapshot = await getDocs(query(categoriesCollection, orderBy('name')));
    
    const categories = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Category[];
    
    console.log('Categories fetched:', categories.length);
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Failed to fetch categories');
  }
};

/**
 * Fetch all categories (alias for getCategories for consistency)
 */
export const fetchAllCategories = async (): Promise<Category[]> => {
  return getCategories();
};

/**
 * Create a new category
 */
export const createCategory = async (name: string, description?: string): Promise<Category> => {
  try {
    // Check if category already exists (case-insensitive)
    const categoriesCollection = collection(db, 'categories');
    const q = query(categoriesCollection, where('name', '==', name.trim()));
    const existingSnapshot = await getDocs(q);
    
    if (!existingSnapshot.empty) {
      throw new Error('Category already exists');
    }

    const now = Timestamp.now();
    const categoryData = {
      name: name.trim(),
      description: description?.trim() || '',
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(categoriesCollection, categoryData);
    
    return {
      id: docRef.id,
      name: categoryData.name,
      description: categoryData.description,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
    };
  } catch (error) {
    console.error('Error creating category:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to create category');
  }
};

/**
 * Update an existing category
 */
export const updateCategory = async (id: string, name: string, description?: string): Promise<void> => {
  try {
    const categoryDoc = doc(db, 'categories', id);
    const updateData = {
      name: name.trim(),
      description: description?.trim() || '',
      updatedAt: Timestamp.now(),
    };

    await updateDoc(categoryDoc, updateData);
  } catch (error) {
    console.error('Error updating category:', error);
    throw new Error('Failed to update category');
  }
};

/**
 * Delete a category
 */
export const deleteCategory = async (id: string): Promise<void> => {
  try {
    const categoryDoc = doc(db, 'categories', id);
    await deleteDoc(categoryDoc);
  } catch (error) {
    console.error('Error deleting category:', error);
    throw new Error('Failed to delete category');
  }
};

/**
 * Search categories by name
 */
export const searchCategories = async (searchTerm: string): Promise<Category[]> => {
  try {
    const categories = await getCategories();
    const lowercaseSearch = searchTerm.toLowerCase();
    
    return categories.filter(category => 
      category.name.toLowerCase().includes(lowercaseSearch)
    );
  } catch (error) {
    console.error('Error searching categories:', error);
    throw new Error('Failed to search categories');
  }
}; 